import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import {
  AiAgentsNamespaceDeleteKnowledgeBaseArgs,
  Mutation,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { useShowConfirmationModal } from 'src/components/general/feedback/hooks/use-show-confirmation-modal';
import { useShowNotification } from 'src/components/general/feedback/hooks/use-show-notification';
import {
  ConfirmationModalDeleteFooter,
  ConfirmationModalMessage,
  ConfirmationModalTitle,
  useConfirmationModalSize,
} from 'src/components/pages/ai-agents/components/confirmation-modal-parts';
import { InUseItemsList } from 'src/components/pages/ai-agents/components/in-use-items-list';
import { DELETE_KNOWLEDGE_BASE_MUTATION } from 'src/components/pages/ai-agents/knowledge-bases/queries';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';

type DeletableKnowledgeBase = {
  id: string;
  aiAgents: { id: string; name: string }[];
};

export const useDeleteKnowledgeBase = ({
  onDeleted,
  refetchList = false,
}: { onDeleted?: VoidFunction; refetchList?: boolean } = {}) => {
  const { t } = useTranslation(['aiAgents']);
  const { id: companyId } = useCurrentInstitution();
  const showConfirmationModal = useShowConfirmationModal();
  const showNotification = useShowNotification();
  const handleGraphQLError = useHandleGraphQLError();
  const modalSize = useConfirmationModalSize();

  const [deleteKnowledgeBase] = useMutation<
    Pick<Mutation, 'aiAgents'>,
    AiAgentsNamespaceDeleteKnowledgeBaseArgs
  >(DELETE_KNOWLEDGE_BASE_MUTATION, {
    onError: (error) => {
      const rejection = error.graphQLErrors.find(
        ({ extensions }) => extensions?.rawCode === 'AI_AGENT_KNOWLEDGE_BASE_REJECTED',
      );

      if (!rejection) {
        handleGraphQLError(error);

        return;
      }

      const detail = rejection.extensions?.detail as { message?: string } | undefined;

      showNotification({
        type: 'error',
        title: t('aiAgents:pages.knowledgeBases.delete.rejected'),
        message: detail?.message,
      });
    },
  });

  return useCallback(
    (knowledgeBase: DeletableKnowledgeBase) => {
      if (knowledgeBase.aiAgents.length > 0) {
        showConfirmationModal({
          testId: 'knowledge-base-delete-blocked-modal',
          ...modalSize,
          title: (
            <ConfirmationModalTitle>
              {t('aiAgents:pages.knowledgeBases.delete.blocked.title')}
            </ConfirmationModalTitle>
          ),
          message: (
            <InUseItemsList
              testId="knowledge-base-delete-blocked-message"
              intro={t('aiAgents:pages.knowledgeBases.delete.blocked.intro')}
              items={knowledgeBase.aiAgents.map(({ id, name }) => ({ id, title: name }))}
            >
              <ConfirmationModalMessage>
                <p>{t('aiAgents:pages.knowledgeBases.delete.blocked.unlinkFirst')}</p>
                <p>{t('aiAgents:pages.knowledgeBases.delete.blocked.prevent')}</p>
              </ConfirmationModalMessage>
            </InUseItemsList>
          ),
          footerContent: () => null,
        });

        return;
      }

      const onConfirm = async () => {
        const result = await deleteKnowledgeBase({
          variables: { input: { companyId, id: knowledgeBase.id } },
          refetchQueries: refetchList ? ['KnowledgeBasesList'] : [],
          update: (cache) => {
            cache.evict({
              id: cache.identify({
                id: knowledgeBase.id,
                __typename: 'AIAgentKnowledgeBase',
              }),
            });
            cache.gc();
          },
        });

        if (result.data?.aiAgents.deleteKnowledgeBase.success) {
          showNotification({
            type: 'success',
            title: t('aiAgents:pages.knowledgeBases.delete.success'),
          });
          onDeleted?.();
        }
      };

      showConfirmationModal({
        testId: 'knowledge-base-delete-modal',
        ...modalSize,
        title: (
          <ConfirmationModalTitle>
            {t('aiAgents:pages.knowledgeBases.delete.title')}
          </ConfirmationModalTitle>
        ),
        message: (
          <ConfirmationModalMessage>
            <p>{t('aiAgents:pages.knowledgeBases.delete.notLinked')}</p>
            <p>{t('aiAgents:pages.knowledgeBases.delete.permanent')}</p>
          </ConfirmationModalMessage>
        ),
        footerContent: ({ onClose }) => (
          <ConfirmationModalDeleteFooter
            confirmLabel={t('aiAgents:pages.knowledgeBases.delete.confirm')}
            onConfirm={() => void onConfirm()}
            onClose={onClose}
          />
        ),
      });
    },
    [
      companyId,
      deleteKnowledgeBase,
      modalSize,
      onDeleted,
      refetchList,
      showConfirmationModal,
      showNotification,
      t,
    ],
  );
};
